import { useCallback, useEffect, useId, useRef, useState } from "react";
import { AlertTriangle, BellIcon, Smartphone } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../button/Button";
import { formatNotificationTime, unreadBadgeLabel } from "@/lib/notificationTime";
import {
  getAdminNotifications,
  markAdminNotificationsSeen,
} from "@/services/notifications.service";
import type { AdminNotification, AdminNotificationFeed } from "@/types/notifications";

// Relecture de la cloche : chaque minute, et au retour sur l'onglet.
const REFRESH_MS = 60_000;

const bellLabel = (unread: number) =>
  unread === 0
    ? "Notifications"
    : `Notifications, ${unread} ${unread > 1 ? "nouvelles" : "nouvelle"}`;

const NotificationContent = ({ notification }: { notification: AdminNotification }) => {
  const isAppleAlert = notification.type === "apple_product_alert";
  const Icon = isAppleAlert ? AlertTriangle : Smartphone;
  return (
    <div className="flex gap-3">
      <Icon
        aria-hidden="true"
        className={`mt-0.5 h-4 w-4 shrink-0 ${isAppleAlert ? "text-red-600" : "text-emerald-600"}`}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p
            className={`text-sm leading-snug ${notification.isNew ? "font-semibold" : "font-medium"}`}
          >
            {notification.title}
          </p>
          {notification.isTest && (
            <span className="shrink-0 rounded-full border border-yellow-200 bg-yellow-100/70 px-2 py-0.5 text-xs font-medium text-yellow-700">
              Test
            </span>
          )}
        </div>
        <p className="mt-0.5 break-words text-xs text-muted-foreground">{notification.message}</p>
        <time
          dateTime={notification.createdAt}
          className="mt-1 block text-xs text-muted-foreground"
        >
          {formatNotificationTime(notification.createdAt)}
        </time>
      </div>
    </div>
  );
};

const NotificationBell: React.FC = () => {
  const [feed, setFeed] = useState<AdminNotificationFeed | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  const refresh = useCallback(async () => {
    const response = await getAdminNotifications();
    if (!response.ok || !response.data) {
      setFailed(true);
      return null;
    }
    setFailed(false);
    setFeed(response.data);
    return response.data;
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), REFRESH_MS);
    const onFocus = () => void refresh();
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [refresh]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // Relit la liste, puis marque vu jusqu'à la plus récente affichée : une
  // notification arrivée entre-temps reste nouvelle.
  const openPanel = async () => {
    setOpen(true);
    const latest = await refresh();
    const newest = latest?.items[0];
    if (!latest || !newest || latest.unreadCount === 0) return;
    const seen = await markAdminNotificationsSeen(newest.createdAt);
    if (seen.ok && seen.data) {
      const { unreadCount } = seen.data;
      setFeed((current) => (current ? { ...current, unreadCount } : current));
    }
  };

  const unread = feed?.unreadCount ?? 0;
  const badge = unreadBadgeLabel(unread);
  const items = feed?.items ?? [];

  return (
    <div ref={containerRef} className="relative">
      <Button
        variant="outline"
        size="icon-lg"
        className="relative rounded-full border-0 font-semibold"
        aria-label={bellLabel(unread)}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => (open ? setOpen(false) : void openPanel())}
      >
        {badge && (
          <span
            data-testid="notification-badge"
            className="absolute top-1 right-1 inline-flex h-4 min-w-4 translate-x-1/2 -translate-y-1/2 transform items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-fg-l"
          >
            {badge}
          </span>
        )}
        <BellIcon className="w-6! h-6!" />
      </Button>
      {open && (
        <div
          id={panelId}
          role="region"
          aria-label="Notifications"
          className="absolute right-0 top-full z-50 mt-2 w-96 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border bg-card shadow-lg"
        >
          <p className="border-b px-4 py-3 text-sm font-semibold">Notifications</p>
          {failed && (
            <p className="px-4 py-3 text-sm text-red-600">
              Impossible de charger les notifications.
            </p>
          )}
          {!failed && feed && items.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">
              Aucune notification pour l'instant.
            </p>
          )}
          {items.length > 0 && (
            <ul className="max-h-[28rem] divide-y overflow-y-auto">
              {items.map((notification) => (
                <li
                  key={notification.id}
                  data-new={notification.isNew}
                  className={notification.isNew ? "bg-primary/5" : undefined}
                >
                  {notification.actionUrl ? (
                    <Link
                      to={notification.actionUrl}
                      onClick={() => setOpen(false)}
                      className="block px-4 py-3 hover:bg-muted/50"
                    >
                      <NotificationContent notification={notification} />
                    </Link>
                  ) : (
                    <div className="px-4 py-3">
                      <NotificationContent notification={notification} />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
